// Marketing Layout — Lot 14B.
// Shell public partagé par toutes les pages marketing.
// Design premium noir / blanc / lime, sémantique HTML stricte, mobile-first.

import { Link, NavLink, Outlet } from "react-router-dom";
import Logo from "@/components/Logo";
import { ArrowRight, Menu, X } from "lucide-react";
import { useState } from "react";

const NAV_LINKS = [
  { to: "/fonctionnalites", label: "Fonctionnalités", testId: "nav-features" },
  { to: "/tarifs", label: "Tarifs", testId: "nav-pricing" },
  { to: "/a-propos", label: "À propos", testId: "nav-about" },
  { to: "/contact", label: "Contact", testId: "nav-contact" },
];

const FOOTER_LINKS = {
  produit: [
    { to: "/fonctionnalites", label: "Fonctionnalités" },
    { to: "/tarifs", label: "Tarifs" },
  ],
  entreprise: [
    { to: "/a-propos", label: "À propos" },
    { to: "/contact", label: "Contact" },
  ],
  legal: [
    { to: "/mentions-legales", label: "Mentions légales" },
  ],
};

const MarketingHeader = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <header
      data-testid="marketing-header"
      className="sticky top-0 z-40 border-b border-black/5 bg-white/80 backdrop-blur-md"
    >
      <div className="mx-auto max-w-6xl px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="marketing-logo" aria-label="WYVIO — accueil">
          <Logo variant="full" size="sm" rounded="rounded-md" />
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Navigation principale">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              className={({ isActive }) =>
                `text-[13.5px] font-medium tracking-tight transition-colors ${
                  isActive ? "text-[#111]" : "text-[#6B7280] hover:text-[#111]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/login"
            data-testid="marketing-login-btn"
            className="text-[13.5px] font-medium text-[#111] hover:text-black"
          >
            Se connecter
          </Link>
          <Link
            to="/contact"
            data-testid="marketing-cta-header"
            className="h-9 inline-flex items-center gap-1.5 rounded-full bg-[#111111] text-white text-[13px] font-medium px-4 hover:bg-black transition-colors"
          >
            Demander une démo
            <ArrowRight size={14} strokeWidth={2.25} />
          </Link>
        </div>

        <button
          className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full text-[#111]"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
          data-testid="marketing-mobile-toggle"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {mobileOpen && (
        <div
          data-testid="marketing-mobile-menu"
          className="md:hidden border-t border-black/5 bg-white"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                className="text-[15px] font-medium text-[#111]"
              >
                {l.label}
              </NavLink>
            ))}
            <div className="h-px bg-black/5 my-2" />
            <Link
              to="/login"
              onClick={() => setMobileOpen(false)}
              className="text-[15px] font-medium text-[#111]"
            >
              Se connecter
            </Link>
            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              className="h-11 inline-flex items-center justify-center rounded-full bg-[#111] text-white text-[14px] font-medium"
            >
              Demander une démo
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

const MarketingFooter = () => (
  <footer
    data-testid="marketing-footer"
    className="mt-32 border-t border-black/5 bg-white"
  >
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2 md:col-span-1">
          <Logo variant="full" size="sm" rounded="rounded-md" />
          <p className="mt-4 text-[13px] text-[#6B7280] leading-relaxed max-w-[220px]">
            Le suivi intelligent de vos ventes automobiles.
          </p>
        </div>
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-wider text-[#111]">Produit</p>
          <ul className="mt-4 space-y-2.5">
            {FOOTER_LINKS.produit.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-[13px] text-[#6B7280] hover:text-[#111]">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-wider text-[#111]">Entreprise</p>
          <ul className="mt-4 space-y-2.5">
            {FOOTER_LINKS.entreprise.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-[13px] text-[#6B7280] hover:text-[#111]">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-wider text-[#111]">Légal</p>
          <ul className="mt-4 space-y-2.5">
            {FOOTER_LINKS.legal.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-[13px] text-[#6B7280] hover:text-[#111]">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="mt-14 pt-6 border-t border-black/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <p className="text-[12px] text-[#6B7280]">© {new Date().getFullYear()} WYVIO. Tous droits réservés.</p>
        <p className="text-[12px] text-[#6B7280]">
          Éditeur : SimpliciCar Toulon Est · <Link to="/mentions-legales" className="hover:text-[#111] underline decoration-[#6B7280]/30 underline-offset-2">mentions légales</Link>
        </p>
      </div>
    </div>
  </footer>
);

const MarketingLayout = () => {
  return (
    <div className="min-h-screen bg-white text-[#111] font-[Inter,sans-serif]" data-testid="marketing-layout">
      <MarketingHeader />
      <Outlet />
      <MarketingFooter />
    </div>
  );
};

export default MarketingLayout;
